import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiSettings, FiLoader, FiPlus } from 'react-icons/fi';
import { configuracoesAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { ConfiguracaoCard, ConfiguracoesFiltros, ConfiguracoesPaginacao } from '../components/configuracoes';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.error[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const Configuracoes = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [configuracoes, setConfiguracoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filtros, setFiltros] = useState({
    search: '',
    id_cliente: '',
    page: 1,
    limit: 10
  });

  // Carregar clientes disponíveis
  useEffect(() => {
    const carregarClientes = async () => {
      try {
        const response = await configuracoesAPI.listarClientes();
        if (response.data.success) {
          const clientesDisponiveis = response.data.data || [];
          setClientes(clientesDisponiveis);

          if (clientesDisponiveis.length === 1 && clientesDisponiveis[0]?.id != null) {
            const clienteId = clientesDisponiveis[0].id.toString();
            const filtrosAtualizados = {
              ...filtros,
              id_cliente: clienteId
            };
            setFiltros(filtrosAtualizados);
            carregarConfiguracoes(filtrosAtualizados);
          }
        } else {
          setClientes([]);
        }
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      }
    };

    if (isAdmin()) {
      carregarClientes();
    }
  }, [isAdmin]);

  // Carregar configurações
  const carregarConfiguracoes = async (params) => {
    try {
      setLoading(true);
      setError(null);

      const response = await configuracoesAPI.listar(params);
      
      if (response.data.success) {
        setConfiguracoes(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Erro ao carregar configurações');
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Carregar configurações na inicialização
  useEffect(() => {
    carregarConfiguracoes(filtros);
  }, []);

  // Atualizar filtros
  const handleFiltrosChange = (novosFiltros) => {
    setFiltros({
      ...novosFiltros,
      page: 1, // Reset para primeira página quando mudar filtros
      limit: filtros.limit || 10 // Manter o limite atual
    });
  };

  // Buscar com filtros (aceita parâmetros opcionais para usar filtros específicos)
  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarConfiguracoes({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarConfiguracoes(filtrosParaBusca);
  };

  // Mudar página
  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarConfiguracoes(novosFiltros);
  };

  // Mudar registros por página
  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = { 
      ...filtros, 
      limit: newLimit,
      page: 1 // Reset para primeira página quando mudar o limite
    };
    setFiltros(novosFiltros);
    carregarConfiguracoes(novosFiltros);
  };

  // Determinar se deve mostrar filtro de clientes
  const shouldShowClienteFilter = clientes.length > 1;

  // Handler para adicionar configuração
  const handleAdicionar = () => {
    navigate('/configuracoes/novo');
  };

  // Handler para editar configuração
  const handleEditar = (configuracao) => {
    if (configuracao.uuid) {
      navigate(`/configuracoes/${configuracao.uuid}/editar`);
    }
  };

  // Renderizar conteúdo baseado no estado
  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando configurações...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Erro ao carregar configurações</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarConfiguracoes(filtros)}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (configuracoes.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiSettings />
          </EmptyIcon>
          <EmptyTitle>Nenhuma configuração encontrada</EmptyTitle>
          <EmptyText>
            Não foram encontradas configurações com os filtros aplicados.
          </EmptyText>
        </EmptyState>
      );
    }

    return (
      <>
        <CardsGrid>
          {configuracoes.map((configuracao) => (
            <ConfiguracaoCard 
              key={configuracao.id || configuracao.uuid} 
              configuracao={configuracao}
              onEdit={handleEditar}
            />
          ))}
        </CardsGrid>

        {pagination.totalPages > 1 && (
          <ConfiguracoesPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Configurações">
      <Container>
        <Header>
          <AddButton onClick={handleAdicionar}>
            <FiPlus />
            Adicionar Configuração
          </AddButton>
        </Header>

        <Content>
          <ConfiguracoesFiltros
            filtros={filtros}
            onFiltrosChange={handleFiltrosChange}
            onBuscar={handleBuscar}
            loading={loading}
            adminClientes={shouldShowClienteFilter ? clientes : []}
          />

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default Configuracoes;

